import type { AsyncDataOptions } from '#app'

export type KeysOf<T> = Array<
  T extends T // Include all keys of union types, not just common keys
    ? keyof T extends string
      ? keyof T
      : never
    : never
>

type CacheMeta = {
  fetchedAt: string
  locale: string
}

function getMetaKey(key: string) {
  return `_meta:${key}`
}

function setCacheMeta(
  nuxtApp: ReturnType<typeof useNuxtApp>,
  key: string,
  locale: string
) {
  const metaKey = getMetaKey(key)
  nuxtApp.payload.data[metaKey] = {
    fetchedAt: new Date().toISOString(),
    locale,
  } as CacheMeta
}

function getCacheMeta(nuxtApp: ReturnType<typeof useNuxtApp>, key: string) {
  const metaKey = getMetaKey(key)
  return nuxtApp.payload.data[metaKey] as CacheMeta | undefined
}

function isExpired(
  fetchedAt: Date,
  fetchedLocale: string,
  locale: string,
  expirationTime = 10 * 60 * 1000
) {
  const expirationDate = new Date(fetchedAt)
  expirationDate.setTime(expirationDate.getTime() + expirationTime)
  return expirationDate < new Date() || fetchedLocale !== locale
}

export default async function useCachedAsyncData<
  ResT,
  NuxtErrorDataT = unknown,
  DataT = ResT,
  PickKeys extends KeysOf<DataT> = KeysOf<DataT>,
  DefaultT = undefined,
>(
  key: MaybeRefOrGetter<string>,
  handler: () => Promise<ResT>,
  options?: Omit<
    AsyncDataOptions<ResT, DataT, PickKeys, DefaultT>,
    'getCachedData'
  >,
  expirationTime: number = 10 * 60 * 1000
) {
  const nuxtApp = useNuxtApp()
  const { locale } = useI18n()

  const res = useAsyncData<ResT, NuxtErrorDataT, DataT, PickKeys, DefaultT>(
    key,
    handler,
    {
      ...options,
      transform: async (_result) => {
        // Store metadata alongside the actual data
        setCacheMeta(nuxtApp, toValue(key), locale.value)

        let result = _result as unknown as DataT

        if (options?.transform) {
          result = await options.transform(_result)
        }

        return result
      },
      getCachedData: (key) => {
        const cachedData = (nuxtApp.payload.data[key] ||
          nuxtApp.static.data[key]) as DataT | undefined
        if (!cachedData) {
          return
        }

        // Get metadata from payload
        const meta = getCacheMeta(nuxtApp, key)

        if (
          meta &&
          isExpired(
            new Date(meta.fetchedAt),
            meta.locale,
            locale.value,
            expirationTime
          )
        ) {
          return
        }

        return cachedData
      },
    }
  )

  const originalRefresh = res.refresh

  async function refresh() {
    const meta = getCacheMeta(nuxtApp, toValue(key))

    if (
      meta &&
      !isExpired(
        new Date(meta.fetchedAt),
        meta.locale,
        locale.value,
        expirationTime
      )
    ) {
      return
    } else {
      originalRefresh()
    }
  }

  res.refresh = refresh

  return res
}
